import {
  clientTypeFamily,
  ONBOARDING_FINAL_STEP,
  type BrandKey,
  type ClientTypeFamily,
  type ClientTypeValue,
} from "@school-sales-ai/shared";

/**
 * The wizard's step list. See docs/plans/client-onboarding-wizard.md §4 for why the
 * order is what it is — in particular, the deployment must exist (step 5) before the
 * agent can be tested (step 6).
 *
 * Steps 8 (WhatsApp) and 9 (users) sit outside the completion gate and are not part
 * of the wizard: neither should block a client from having a working web agent, and
 * WhatsApp can wait days on Meta approval.
 */

export type WizardStep = {
  n: number;
  /** Shown in the stepper. Keep it to one or two words. */
  short: string;
  /** Shown as the step heading. */
  title: string;
};

export const WIZARD_STEPS: WizardStep[] = [
  { n: 1, short: "Cliente", title: "Datos del cliente" },
  { n: 2, short: "Contexto", title: "Contexto del cliente" },
  { n: 3, short: "Catálogo", title: "Catálogo" },
  { n: 4, short: "Conocimiento", title: "Base de conocimiento" },
  { n: 5, short: "Agente", title: "Script y agente" },
  { n: 6, short: "Prueba", title: "Probar el agente" },
  { n: 7, short: "Publicar", title: "Activar y publicar" },
];

export const FIRST_STEP = 1;
export const LAST_STEP = ONBOARDING_FINAL_STEP;

/**
 * Route param for a client that does not exist yet: `/onboarding/new` is step 1 in
 * create mode. Not a uuid, so it can never collide with a real client id.
 */
export const NEW_CLIENT_PARAM = "new";

/** Clamps a stored `current_step` to a step this wizard actually renders. */
export function clampStep(step: number): number {
  if (!Number.isFinite(step)) return FIRST_STEP;
  // A client that has moved on to the post-completion tasks (8, 9) resumes on the
  // last wizard step rather than on a step with nothing to show.
  return Math.min(Math.max(Math.trunc(step), FIRST_STEP), LAST_STEP);
}

/**
 * Whether the wizard speaks of a school or of a business — only its copy follows this.
 *
 * The segment decides when there is one; a client still without a segment falls back to
 * its brand, which is the field the operator picks first.
 */
export function wizardFamily(client: {
  clientType: ClientTypeValue | null;
  brand?: BrandKey;
}): ClientTypeFamily {
  if (client.clientType) return clientTypeFamily(client.clientType);
  return client.brand === "general" ? "business" : "school";
}
